import React, { useState } from 'react';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Loader2,
  ListChecks,
  Calendar,
  CheckCircle,
  XCircle,
  Play,
  Flag,
  DollarSign,
  Clock,
  Star,
  MessageSquare,
  Megaphone,
  Settings,
  Film,
} from 'lucide-react';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
  useDeleteNotification,
} from '../../services/notificationService';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

const VendorNotifications = () => {
  const { showToast } = useToastContext();
  const [filter, setFilter] = useState('all'); // all, unread, read

  const { data: notificationsData, isLoading } = useNotifications({ per_page: 50 });
  
  // Handle the API response structure - data is inside data.data for paginated responses
  const notifications = notificationsData?.data?.data || notificationsData?.data || [];

  const markAsReadMutation = useMarkNotificationAsRead({
    onSuccess: () => {
      showToast('Notification marked as read', 'success');
    },
  });

  const markAllAsReadMutation = useMarkAllNotificationsAsRead({
    onSuccess: () => {
      showToast('All notifications marked as read', 'success');
    },
  });

  const deleteMutation = useDeleteNotification({
    onSuccess: () => {
      showToast('Notification deleted', 'success');
    },
  });

  const filteredNotifications = Array.isArray(notifications) ? notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    return true;
  }) : [];

  const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.is_read).length : 0;
  const readCount = Array.isArray(notifications) ? notifications.length - unreadCount : 0;

  const handleMarkAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  const handleMarkAllAsRead = () => {
    if (unreadCount > 0) {
      markAllAsReadMutation.mutate();
    }
  };

  const handleDelete = (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      deleteMutation.mutate(notificationId);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_request':
        return {
          Icon: Calendar,
          color: 'text-sky-600 dark:text-sky-400',
          bg: 'bg-sky-50 dark:bg-sky-950/40 border-sky-100 dark:border-sky-900/50',
        };
      case 'booking_confirmed':
        return {
          Icon: CheckCircle,
          color: 'text-emerald-600 dark:text-emerald-400',
          bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50',
        };
      case 'booking_cancelled':
        return {
          Icon: XCircle,
          color: 'text-red-600 dark:text-red-400',
          bg: 'bg-red-50 dark:bg-red-950/40 border-red-100 dark:border-red-900/50',
        };
      case 'booking_in_progress':
        return {
          Icon: Play,
          color: 'text-primary-600 dark:text-primary-400',
          bg: 'bg-primary-50 dark:bg-primary-950/40 border-primary-100 dark:border-primary-900/50',
        };
      case 'booking_completed':
        return {
          Icon: Flag,
          color: 'text-emerald-600 dark:text-emerald-400',
          bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50',
        };
      case 'payment_received':
        return {
          Icon: DollarSign,
          color: 'text-emerald-600 dark:text-emerald-400',
          bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900/50',
        };
      case 'payment_pending':
        return {
          Icon: Clock,
          color: 'text-amber-600 dark:text-amber-400',
          bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-900/50',
        };
      case 'event_started':
        return {
          Icon: Film,
          color: 'text-primary-600 dark:text-primary-400',
          bg: 'bg-primary-50 dark:bg-primary-950/40 border-primary-100 dark:border-primary-900/50',
        };
      case 'event_ended':
        return {
          Icon: Flag,
          color: 'text-gray-600 dark:text-gray-300',
          bg: 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
        };
      case 'review_received':
        return {
          Icon: Star,
          color: 'text-amber-600 dark:text-amber-400',
          bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-900/50',
        };
      case 'message_received':
        return {
          Icon: MessageSquare,
          color: 'text-sky-600 dark:text-sky-400',
          bg: 'bg-sky-50 dark:bg-sky-950/40 border-sky-100 dark:border-sky-900/50',
        };
      case 'broadcast':
        return {
          Icon: Megaphone,
          color: 'text-primary-600 dark:text-primary-400',
          bg: 'bg-primary-50 dark:bg-primary-950/40 border-primary-100 dark:border-primary-900/50',
        };
      case 'reminder':
        return {
          Icon: Clock,
          color: 'text-amber-600 dark:text-amber-400',
          bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-100 dark:border-amber-900/50',
        };
      case 'system':
        return {
          Icon: Settings,
          color: 'text-gray-600 dark:text-gray-300',
          bg: 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
        };
      default:
        return {
          Icon: Bell,
          color: 'text-gray-600 dark:text-gray-300',
          bg: 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
        };
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getFilterCount = (key) => {
    if (key === 'unread') return unreadCount;
    if (key === 'read') return readCount;
    return Array.isArray(notifications) ? notifications.length : 0;
  };

  const emptyCopy =
    filter === 'unread'
      ? "You're all caught up — no unread notifications."
      : filter === 'read'
        ? 'No read notifications yet.'
        : "You don't have any notifications yet.";

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Notifications"
        subtitle={
          unreadCount > 0
            ? `Booking, payment, and support alerts — ${unreadCount} unread.`
            : 'Booking, payment, and support alerts land here so you never miss a request.'
        }
        actions={
          unreadCount > 0 ? (
            <Button
              onClick={handleMarkAllAsRead}
              icon={CheckCheck}
              variant="outline"
              className="!rounded-xl"
              disabled={markAllAsReadMutation.isPending}
              loading={markAllAsReadMutation.isPending}
            >
              Mark All as Read
            </Button>
          ) : null
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.notifications.title}
        steps={VENDOR_PAGE_GUIDES.notifications.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Notification filter"
          >
            {FILTER_TABS.map((tab) => {
              const isActive = filter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setFilter(tab.key)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`ml-1.5 tabular-nums ${
                      isActive
                        ? 'text-gray-500 dark:text-gray-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    ({getFilterCount(tab.key)})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading notifications…</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="py-16 sm:py-20 text-center px-4">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                <Bell className="w-7 h-7" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                No notifications
              </h3>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto leading-relaxed">
                {emptyCopy}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications.map((notification) => {
                const { Icon, color, bg } = getNotificationIcon(notification.type);
                return (
                  <div
                    key={notification.id}
                    className={`rounded-xl border p-4 sm:p-5 transition-colors ${
                      !notification.is_read
                        ? 'border-primary-200/80 dark:border-primary-800/40 bg-primary-50/40 dark:bg-primary-950/20 hover:border-primary-300 dark:hover:border-primary-700/60'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 hover:border-gray-300 dark:hover:border-gray-600'
                    } ${notification.is_broadcast ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (notification.is_broadcast) {
                        window.dispatchEvent(
                          new CustomEvent('vendor:showBroadcastModal', {
                            detail: { notification },
                          })
                        );
                      }
                    }}
                  >
                    <div className="flex items-start gap-3.5 sm:gap-4">
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-xl border flex items-center justify-center ${bg}`}
                      >
                        <Icon className={`w-[18px] h-[18px] ${color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                                {notification.title || 'Notification'}
                              </h3>
                              {notification.is_broadcast && (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border border-primary-200 dark:border-primary-800/50 bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300">
                                  Broadcast
                                </span>
                              )}
                              {!notification.is_read && (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 leading-relaxed">
                              {notification.message}
                            </p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-500">
                              <span>{formatTimeAgo(notification.created_at)}</span>
                              <span className="capitalize text-gray-400 dark:text-gray-500">
                                {notification.type?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                          <div
                            className="flex items-center gap-0.5 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {!notification.is_read && (
                              <button
                                type="button"
                                onClick={() => handleMarkAsRead(notification.id)}
                                disabled={markAsReadMutation.isPending}
                                className="p-2 text-gray-400 dark:text-gray-500 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition-colors disabled:opacity-50"
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(notification.id)}
                              disabled={deleteMutation.isPending}
                              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors disabled:opacity-50"
                              title="Delete notification"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorNotifications;
